import React, { useState, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Divider } from 'primereact/divider';
import { classNames } from 'primereact/utils';
import axiosInstance from '../../config/axios.config';
import establishmentService from '../../services/establishmentService';

const CompanyInfoStep = ({
    formData,
    updateFormData,
    errors,
    companyNameAvailable,
    onCompanyNameCheck,
    toast
}) => {
    const [checkingCompanyName, setCheckingCompanyName] = useState(false);

    // Establishment state
    const [establishmentTypes, setEstablishmentTypes] = useState([]);
    const [establishmentFields, setEstablishmentFields] = useState([]);
    const [loadingEstablishment, setLoadingEstablishment] = useState(false);

    // Debounced company name check
    useEffect(() => {
        if (!formData.company_name || formData.company_name.length < 3) {
            onCompanyNameCheck(null);
            return;
        }

        const timer = setTimeout(() => {
            checkCompanyNameAvailability(formData.company_name);
        }, 800);

        return () => clearTimeout(timer);
    }, [formData.company_name]);

    // Check company name availability
    const checkCompanyNameAvailability = async (name) => {
        if (name.length < 3) return;

        setCheckingCompanyName(true);

        try {
            const response = await axiosInstance.post('/company/check-name', {
                company_name: name
            });

            if (response.data.success) {
                onCompanyNameCheck(response.data.available);
            }
        } catch (error) {
            console.error('Company name check error:', error);
        } finally {
            setCheckingCompanyName(false);
        }
    };

    // Get status icon for company name
    const getCompanyNameIcon = () => {
        if (checkingCompanyName) return <i className="pi pi-spin pi-spinner ml-2" />;
        if (companyNameAvailable === true) return <i className="pi pi-check-circle ml-2 text-green-500" />;
        if (companyNameAvailable === false) return <i className="pi pi-times-circle ml-2 text-red-500" />;
        return null;
    };

    // Load establishment types on mount (PUBLIC API - no auth required)
    useEffect(() => {
        const loadEstablishmentTypes = async () => {
            try {
                // Use public API endpoint for company registration
                const response = await axiosInstance.get('/company/establishment/types');
                if (response.data.success) {
                    setEstablishmentTypes(response.data.data);
                }
            } catch (error) {
                console.error('Error loading establishment types:', error);
                if (toast && toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to load establishment types',
                        life: 3000
                    });
                }
            }
        };
        loadEstablishmentTypes();
    }, []);

    // Load establishment requirements when type changes
    useEffect(() => {
        const loadRequirements = async () => {
            if (!formData.type_of_establishment) {
                setEstablishmentFields([]);
                return;
            }

            setLoadingEstablishment(true);
            try {
                // Get field configurations from service (this is frontend-only)
                const fields = establishmentService.getFieldsForType(
                    formData.type_of_establishment
                );
                setEstablishmentFields(fields);
            } catch (error) {
                console.error('Error loading establishment requirements:', error);
            } finally {
                setLoadingEstablishment(false);
            }
        };
        loadRequirements();
    }, [formData.type_of_establishment]);

    // Auto-fill establishment name from company_name when type changes
    useEffect(() => {
        if (!formData.type_of_establishment || !formData.company_name) {
            return;
        }

        // Mapping of establishment types to their name fields
        const establishmentNameFieldMapping = {
            'individual': 'individual_name',
            'proprietorship': 'proprietorship_establishment_name',
            'partnership': 'partnership_establishment_name',
            'llp': 'llp_name',
            'private_limited': 'pvt_ltd_name',
            'public_limited': 'public_ltd_name',
            'one_man_company': 'omc_name',
            'society': 'society_name',
            'trust': 'trust_name',
            'nbfc': 'nbfc_name',
            'bank': 'bank_name',
            'mfi': 'mfi_name'
        };

        const nameField = establishmentNameFieldMapping[formData.type_of_establishment];

        // Only auto-fill if the field is empty
        if (nameField && !formData[nameField]) {
            updateFormData(nameField, formData.company_name);
        }
    }, [formData.type_of_establishment]);

    // Render establishment field
    const renderEstablishmentField = (field) => {
        const value = formData[field.name] || '';
        const error = errors[field.name];

        if (field.conditional && !formData[field.conditional]) {
            return null;
        }

        switch (field.type) {
            case 'text':
                return (
                    <div className="col-12 md:col-6" key={field.name}>
                        <div className="field mb-3">
                            <label htmlFor={field.name} className="font-semibold text-900 text-sm">
                                <i className="pi pi-user mr-2"></i>
                                {field.label} {field.required && <span className="text-red-500">*</span>}
                            </label>
                            <InputText
                                id={field.name}
                                value={value}
                                onChange={(e) => updateFormData(field.name, e.target.value)}
                                placeholder={`Enter ${field.label.toLowerCase()}`}
                                pattern={field.pattern}
                                className={classNames('w-full', { 'p-invalid': error })}
                            />
                            {error && <small className="p-error">{error}</small>}
                        </div>
                    </div>
                );

            case 'number':
                return (
                    <div className="col-12 md:col-6" key={field.name}>
                        <div className="field mb-3">
                            <label htmlFor={field.name} className="font-semibold text-900 text-sm">
                                <i className="pi pi-users mr-2"></i>
                                {field.label} {field.required && <span className="text-red-500">*</span>}
                            </label>
                            <InputText
                                id={field.name}
                                value={value}
                                onChange={(e) => updateFormData(field.name, e.target.value)}
                                placeholder={`Enter ${field.label.toLowerCase()}`}
                                keyfilter="int"
                                className={classNames('w-full', { 'p-invalid': error })}
                            />
                            {error && <small className="p-error">{error}</small>}
                        </div>
                    </div>
                );

            case 'checkbox':
                return (
                    <div className="col-12" key={field.name}>
                        <div className="field mb-3">
                            <div className="flex align-items-center gap-2">
                                <Checkbox
                                    inputId={field.name}
                                    checked={value === true}
                                    onChange={(e) => updateFormData(field.name, e.checked)}
                                />
                                <label htmlFor={field.name} className="font-semibold text-900 text-sm cursor-pointer">
                                    <i className="pi pi-verified mr-2"></i>
                                    {field.label}
                                </label>
                            </div>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="p-fluid">
            {/* Company Information Section */}
            <div className="section-header mb-3">
                <h4 className="m-0 text-900 text-base">
                    <i className="pi pi-building mr-2"></i>
                    Company Information
                </h4>
                <p className="mt-1 mb-0 text-600 text-sm">Basic company details and registration information</p>
            </div>

            <div className="formgrid grid">
                {/* Company Name */}
                <div className="col-12">
                    <div className="field mb-3">
                        <label htmlFor="company_name" className="font-semibold text-900 text-sm">
                            <i className="pi pi-briefcase mr-2"></i>
                            Company Name <span className="text-red-500">*</span>
                            {getCompanyNameIcon()}
                        </label>
                        <InputText
                            id="company_name"
                            value={formData.company_name || ''}
                            onChange={(e) => updateFormData('company_name', e.target.value)}
                            placeholder="Enter your company name"
                            className={classNames('w-full', { 'p-invalid': errors.company_name || companyNameAvailable === false })}
                            maxLength={255}
                        />
                        {errors.company_name && <small className="p-error">{errors.company_name}</small>}
                        {companyNameAvailable === true && (
                            <small className="text-green-500">✓ Company name is available</small>
                        )}
                        {companyNameAvailable === false && (
                            <small className="p-error">This company name is already registered</small>
                        )}
                    </div>
                </div>

                {/* Address */}
                <div className="col-12">
                    <div className="field mb-3">
                        <label htmlFor="address" className="font-semibold text-900 text-sm">
                            <i className="pi pi-map-marker mr-2"></i>
                            Company Address <span className="text-red-500">*</span>
                        </label>
                        <InputTextarea
                            id="address"
                            value={formData.address || ''}
                            onChange={(e) => updateFormData('address', e.target.value)}
                            placeholder="Enter complete registered address"
                            rows={3}
                            className={classNames('w-full', { 'p-invalid': errors.address })}
                            maxLength={500}
                        />
                        <small className="text-500">{formData.address?.length || 0}/500 characters</small>
                        {errors.address && <small className="p-error block mt-1">{errors.address}</small>}
                    </div>
                </div>
            </div>

            <Divider />

            {/* Establishment Details Section */}
            <div className="section-header mb-3">
                <h4 className="m-0 text-900 text-base">
                    <i className="pi pi-sitemap mr-2"></i>
                    Establishment Details
                </h4>
                <p className="mt-1 mb-0 text-600 text-sm">Business structure and establishment information</p>
            </div>

            <div className="formgrid grid">
                {/* Establishment Type Selection */}
                <div className="col-12">
                    <div className="field mb-3">
                        <label htmlFor="type_of_establishment" className="font-semibold text-900 text-sm">
                            <i className="pi pi-building mr-2"></i>
                            Type of Establishment <span className="text-red-500">*</span>
                        </label>
                        <Dropdown
                            id="type_of_establishment"
                            value={formData.type_of_establishment || ''}
                            options={establishmentTypes}
                            onChange={(e) => updateFormData('type_of_establishment', e.value)}
                            placeholder="Select establishment type"
                            className={classNames('w-full', { 'p-invalid': errors.type_of_establishment })}
                            optionLabel="label"
                            optionValue="value"
                        />
                        {errors.type_of_establishment && <small className="p-error">{errors.type_of_establishment}</small>}
                    </div>
                </div>

                {/* Dynamic Fields Based on Establishment Type */}
                {loadingEstablishment ? (
                    <div className="col-12">
                        <div className="text-center py-5">
                            <i className="pi pi-spin pi-spinner text-primary" style={{ fontSize: '2rem' }}></i>
                            <p className="mt-3 text-600">Loading establishment requirements...</p>
                        </div>
                    </div>
                ) : (
                    establishmentFields.length > 0 && establishmentFields.map(field => renderEstablishmentField(field))
                )}
            </div>

            {/* GST Information Section */}
            {formData.type_of_establishment && (
                <>
                    <Divider />
                    <div className="section-header mb-3">
                        <h4 className="m-0 text-900 text-base">
                            <i className="pi pi-id-card mr-2"></i>
                            GST Information
                        </h4>
                        <p className="mt-1 mb-0 text-600 text-sm">Goods and Services Tax registration details</p>
                    </div>

                    <div className="formgrid grid">
                        <div className="col-12">
                            <div className="field mb-3">
                                <div className="flex align-items-center">
                                    <Checkbox
                                        inputId="has_gst_certificate"
                                        checked={formData.has_gst_certificate || false}
                                        onChange={(e) => {
                                            updateFormData('has_gst_certificate', e.checked);
                                            if (!e.checked) {
                                                updateFormData('gst_certificate_number', '');
                                            }
                                        }}
                                    />
                                    <label htmlFor="has_gst_certificate" className="ml-2 font-semibold text-900 cursor-pointer">
                                        Do you have GST Registration?
                                    </label>
                                </div>
                            </div>
                        </div>

                        {formData.has_gst_certificate && (
                            <div className="col-12 md:col-8">
                                <div className="field mb-3">
                                    <label htmlFor="gst_certificate_number" className="font-semibold text-900 text-sm">
                                        <i className="pi pi-id-card mr-2"></i>
                                        GST Number <span className="text-red-500">*</span>
                                    </label>
                                    <InputText
                                        id="gst_certificate_number"
                                        value={formData.gst_certificate_number || ''}
                                        onChange={(e) => updateFormData('gst_certificate_number', e.target.value.toUpperCase())}
                                        placeholder="Enter 15-digit GST number (e.g., 27AAACG1234H1ZM)"
                                        maxLength="15"
                                        className={classNames('w-full', { 'p-invalid': errors.gst_certificate_number })}
                                    />
                                    {formData.gst_certificate_number && formData.gst_certificate_number.length !== 15 && (
                                        <small className="p-error">GST number must be exactly 15 characters</small>
                                    )}
                                    {errors.gst_certificate_number && <small className="p-error">{errors.gst_certificate_number}</small>}
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Bank Account Details Section (Optional) */}
            {formData.type_of_establishment && (
                <>
                    <Divider />
                    <div className="section-header mb-3">
                        <h4 className="m-0 text-900 text-base">
                            <i className="pi pi-wallet mr-2"></i>
                            Bank Account Details (Optional)
                        </h4>
                        <p className="mt-1 mb-0 text-600 text-sm">Banking information for transactions - can be added later</p>
                    </div>

                    <div className="formgrid grid">
                        <div className="col-12 md:col-6">
                            <div className="field mb-3">
                                <label htmlFor="bank_account_name" className="font-semibold text-900 text-sm">
                                    <i className="pi pi-user mr-2"></i>
                                    Account Holder Name
                                </label>
                                <InputText
                                    id="bank_account_name"
                                    value={formData.bank_account_name || ''}
                                    onChange={(e) => updateFormData('bank_account_name', e.target.value)}
                                    placeholder="Enter account holder name"
                                    className={classNames('w-full', { 'p-invalid': errors.bank_account_name })}
                                />
                                {errors.bank_account_name && <small className="p-error">{errors.bank_account_name}</small>}
                            </div>
                        </div>

                        <div className="col-12 md:col-6">
                            <div className="field mb-3">
                                <label htmlFor="account_number" className="font-semibold text-900 text-sm">
                                    <i className="pi pi-credit-card mr-2"></i>
                                    Account Number
                                </label>
                                <InputText
                                    id="account_number"
                                    value={formData.account_number || ''}
                                    onChange={(e) => updateFormData('account_number', e.target.value)}
                                    placeholder="Enter account number (9-18 digits)"
                                    keyfilter="int"
                                    maxLength="18"
                                    className={classNames('w-full', { 'p-invalid': errors.account_number })}
                                />
                                {errors.account_number && <small className="p-error">{errors.account_number}</small>}
                            </div>
                        </div>

                        <div className="col-12 md:col-4">
                            <div className="field mb-3">
                                <label htmlFor="account_type" className="font-semibold text-900 text-sm">
                                    <i className="pi pi-list mr-2"></i>
                                    Type of Account
                                </label>
                                <Dropdown
                                    id="account_type"
                                    value={formData.account_type || ''}
                                    options={[
                                        { label: 'Savings', value: 'savings' },
                                        { label: 'Current', value: 'current' },
                                        { label: 'Overdraft', value: 'overdraft' },
                                        { label: 'Cash Credit', value: 'cash_credit' },
                                        { label: 'NRI', value: 'nri' }
                                    ]}
                                    onChange={(e) => updateFormData('account_type', e.value)}
                                    placeholder="Select account type"
                                    className={classNames('w-full', { 'p-invalid': errors.account_type })}
                                    optionLabel="label"
                                    optionValue="value"
                                />
                                {errors.account_type && <small className="p-error">{errors.account_type}</small>}
                            </div>
                        </div>

                        <div className="col-12 md:col-4">
                            <div className="field mb-3">
                                <label htmlFor="ifsc_code" className="font-semibold text-900 text-sm">
                                    <i className="pi pi-key mr-2"></i>
                                    IFSC Code
                                </label>
                                <InputText
                                    id="ifsc_code"
                                    value={formData.ifsc_code || ''}
                                    onChange={(e) => updateFormData('ifsc_code', e.target.value.toUpperCase())}
                                    placeholder="e.g., SBIN0001234"
                                    maxLength="11"
                                    className={classNames('w-full', { 'p-invalid': errors.ifsc_code })}
                                />
                                {errors.ifsc_code && <small className="p-error">{errors.ifsc_code}</small>}
                            </div>
                        </div>

                        <div className="col-12 md:col-4">
                            <div className="field mb-3">
                                <label htmlFor="bank_name" className="font-semibold text-900 text-sm">
                                    <i className="pi pi-building mr-2"></i>
                                    Bank Name
                                </label>
                                <InputText
                                    id="bank_name"
                                    value={formData.bank_name || ''}
                                    onChange={(e) => updateFormData('bank_name', e.target.value)}
                                    placeholder="Enter bank name"
                                    className={classNames('w-full', { 'p-invalid': errors.bank_name })}
                                />
                                {errors.bank_name && <small className="p-error">{errors.bank_name}</small>}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default CompanyInfoStep;
